import { Box, Button, Grid } from "@mui/material";
import { useContext, useEffect, useState } from "react";
import { getWorkOrderDetails, postUpdateWorkOrderStatus } from "../../../data/mahiApiConnections";
import { hikoAlert, hikoException } from "../../../utils/HikoAlert";
import AlertLoading from "../../navigation/AlertLoading";
import { MahiContext } from "../../state/MahiContext";
import IcpInspectionLink from "./IcpInspectionLink";
import IcpInspectionDetailsAccordion from "./IcpInspectionDetailsAccordion";
import { isValidDateObject, isWorkOrderStatusEnabledForInspection } from "../../../utils/utilityFunctions";
import WorkOrderOnHoldPanel from "./WorkOrderOnHoldPanel";
import WorkOrderNotesPanel from "./WorkOrderNotesPanel";
import { asyncFetchMahiBff } from "../../../data/hikoFetch";
import { formatISO } from "date-fns";
import { DebugBox } from "@northpowerdev/np-react-utils";

export default function ConnectIcpInspectionDetails() {

    const [pageLoading, setPageLoading] = useState(false)
    const [icpNumber, setIcpNumber] = useState()
    const [workOrderId, setWorkOrderId] = useState()
    const [workOrderDetails, setWorkOrderDetails] = useState()
    const [isInspectionEnabled, setIsInspectionEnabled] = useState(false)
    const [postsLoading, setPostsLoading] = useState(false)
    const [postsList, setPostsList] = useState([])

    const mahiContext = useContext(MahiContext)

    useEffect(() => {

        let didCancel = false;
        const abortController = new AbortController()

        const asyncGetWorkOrder = async (someWorkOrderId, abortSignal) => {
            setPageLoading(true)
            try {
                const workOrderResponse = await getWorkOrderDetails(mahiContext, someWorkOrderId, abortSignal)
                if (!didCancel) {
                    setWorkOrderDetails(workOrderResponse)
                    const inspectionEnabled = isWorkOrderStatusEnabledForInspection(workOrderResponse?.workOrder?.status)
                    setIsInspectionEnabled(inspectionEnabled)
                }
            } catch {
                hikoAlert('No work order found', 'We could not find details for your work order')
            } finally {
                if (!didCancel) {
                    setPageLoading(false)
                }
            }
        }

        console.log('ConnectIcpInspectionDetails useEffect')
        const searchParams = new URLSearchParams(window.location.search)
        const paramIcpNumber = searchParams.get('icp_number');
        const paramWorkOrderId = searchParams.get('work_order_id');
        setIcpNumber(paramIcpNumber)
        setWorkOrderId(paramWorkOrderId)
        asyncGetWorkOrder(paramWorkOrderId, abortController.signal)
        asyncFetchMahiBff(mahiContext, `/bff/icp-livening-service/api/new-connections/work-order/${paramWorkOrderId}/get-posts`, setPostsLoading, setPostsList)

        return (() => { didCancel = true })

        // eslint-disable-next-line
    }, []);

    const handleOnHoldPanelStatusChange = async (workOrderStatus, onHoldReason, scheduledDate) => {
        console.log('handleOnHoldPanelStatusChange workOrderStatus, onHoldReason, scheduledDate', workOrderStatus, onHoldReason, scheduledDate)
        let savedOK = false
        try {
            savedOK = await postUpdateWorkOrderStatus(mahiContext, workOrderId, workOrderStatus, onHoldReason, scheduledDate)
        } catch (e) {
            console.error(e)
            hikoException('We could not update work order status', e)
        } finally {
            setPageLoading(false)
        }

        if (savedOK) {
            //
            // Could re-fetch the work order via getWorkOrderDetails but we trust
            // that web service POST has updated workOrderStatus and just change the status in the client. 
            //
            console.log('handleOnHoldPanelStatusChange saved OK. Updating status to', workOrderStatus)
            const newWorkOrder = { ...workOrderDetails.workOrder }
            newWorkOrder.status = workOrderStatus
            newWorkOrder.onHoldReason = onHoldReason
            if (isValidDateObject(scheduledDate)) {
                newWorkOrder.scheduledDate = formatISO(scheduledDate)
            }
            setWorkOrderDetails({ ...workOrderDetails, workOrder: newWorkOrder })
        }
        return savedOK
    }

    const handleNoteAdded = (noteObject) => {
        console.log('ConnectIcpInspectionDetails handleNoteAdded noteObject', noteObject)
        const newPostsList = [...postsList]
        newPostsList.push(noteObject)
        setPostsList(newPostsList)
    }

    const inspectionLinkSx = isInspectionEnabled ? { position: "fixed", bottom: (theme) => theme.spacing(2), right: (theme) => theme.spacing(2), zIndex: 100 } : {}

    return (
        <Box className="Page">
            <Grid container spacing={2} sx={{ mb: 2 }}>
                <Grid item xs={12}>
                    <Button variant='text' href="/connect/list_icp_inspections" sx={{ ml: -1, mt: -1 }}>Back</Button>
                    <IcpInspectionDetailsAccordion
                        title="ICP Inspection Details"
                        workOrderId={workOrderId}
                        icpNumber={icpNumber}
                        workOrderDetails={workOrderDetails}
                    />
                </Grid>
                <Grid item xs={12} md={6}>
                    <Box>
                        <WorkOrderOnHoldPanel
                            workOrderNumber={workOrderDetails?.workOrder?.workOrderNumber}
                            status={workOrderDetails?.workOrder?.status}
                            scheduledDate={workOrderDetails?.workOrder?.scheduledDateXX}
                            onHoldReason={workOrderDetails?.workOrder?.onHoldReason}
                            onStatusChange={handleOnHoldPanelStatusChange}
                        />
                    </Box>
                </Grid>
                <Grid item xs={12} md={6}>
                    <Box>
                        <WorkOrderNotesPanel
                            workOrderId={workOrderId}
                            icpNumber={icpNumber}
                            isLoading={postsLoading}
                            notes={postsList}
                            noteAdded={handleNoteAdded}
                        />
                    </Box>
                </Grid>
                <Grid item xs={12}>
                    <Box>
                        {
                            pageLoading
                                ?
                                <AlertLoading spinner={pageLoading} />
                                :
                                <IcpInspectionLink workOrderDetails={workOrderDetails} inspectionEnabled={isInspectionEnabled} sx={inspectionLinkSx} />
                        }
                    </Box>
                </Grid>

            </Grid>


            <DebugBox title="work order id">{workOrderId}</DebugBox>
            <DebugBox title="work order details" stringify>{workOrderDetails}</DebugBox>

        </Box>
    )
}